import Header from "../components/Header";
import Footer from "../components/Footer";

const abs_roles = [
  {
    title: 'Senior Software Engineer (Research & Development)',
    duration: 'Oct 2025 – Present',
    bullets: [
      'Architected and deployed a lean, production-grade GenAI and Retrieval-Augmented Generation (RAG) framework on AWS, optimising infrastructure to handle concurrent requests under a minimal cloud footprint (< $1 per user query).',
      'Mentored junior team members in software architecture, coding practices, and scalable design patterns to cultivate a strong engineering-led team culture.',
      'Engineered highly scalable, automated QA and validation workbenches to perform high-precision ETL processing and data-linkage on national longitudinal datasets exceeding 28M granular records.',
      'Co-developed core software pipelines for the $16.4M Life Course Data Initiative, managing multi-dimensional indexes and turning high-level statistical concepts into reliable, production-ready system features.'
    ]
  },
  {
    title: 'Software Engineer (Census Operations)',
    duration: 'Nov 2024 – Oct 2025',
    bullets: [
      'Designed and coded a pioneering direct sampling algorithm for Gaussian hierarchical models, bypassing legacy MCMC computational bottlenecks to radically optimise background processing times.',
      'Enabled real-time tactical decisions for a $600M+ nationwide field operation by optimising heavy algorithmic workloads to execute on local systems, entirely eliminating cloud computation costs.',
      'Provided technical support across cross-functional, geographically distributed teams by diagnosing and refactoring ad-hoc data management bottlenecks.'
    ]
  },
  {
    title: 'Software Engineer (Geospatial Systems – Contract)',
    duration: 'Jun 2024 – Jul 2025',
    bullets: [
      'Owned and executed the full Software Development Life Cycle (SDLC) to modularise and operationalise theoretical academic research into a high-performance, production-ready Python toolset.',
      'Compressed an intensive 20-minute manual regional-design process down to seconds across 10,000+ regions, future-proofing operational budgets by eliminating long-term staffing constraints.',
      'Developed a robust, automated QA heuristic framework to validate data integrity across legacy datasets, replacing slow, multi-source human verification loops.',
      'Authored complete technical documentation and system integration guides, and led hands-on training workshops to ensure seamless code handovers and system sustainability.'
    ]
  }
];

const bennett_bullets = [
  "Served as the organisation's foundational software engineer, establishing code guidelines, managing full SDLC, and launching a suite of B2B SaaS applications that acquired 110+ weekly business users.",
  'Modernised legacy paper-based workflows by architecting and deploying a secure, full-stack Digital Site Instruction Docket application, eliminating revenue loss and corporate liability stemming from displaced physical files.',
  'Designed and optimised high-performance, GPU-accelerated algorithms using Python, C++, and CUDA for large-scale computer vision and spatial filtering, cutting out hours of manual 3D object "de-noising" work.',
  'Maintained an aggressive prototyping-to-production schedule, rapidly shipping robust web and desktop interfaces utilising Python, React (JavaScript), and SQL.'
];

const gpone_bullets = [
  'Engineered automated backend data pipelines and interactive web dashboards for the award-winning, state-wide QWildlife tracking platform (supporting a program with $3M in annual funding).',
  'Maintained and queried highly scalable geospatial databases hosted on AWS EC2 PostgreSQL instances, optimising index queries and ensuring rapid response times for downstream applications.',
  'Built and trained computer vision models to automate local council infrastructure damage assessment, moving code from experimental R&D into production blueprints for predictive infrastructure savings.',
  'Utilised Figma to blueprint user journeys, wireframes, and mobile UI/UX layouts, aligning customer expectations with efficient data ingestion pipelines.'
];

const experience = [
  {
    company: 'Australian Bureau of Statistics',
    location: 'Brisbane, AU',
    duration: 'Jun 2024 – Present',
    roles: abs_roles,
    tags: 'Python, AWS, RAG/GenAI, PostgreSQL, ETL'
  },
  {
    company: 'Bennett & Bennett (SiteDE)',
    location: 'Brisbane, AU',
    duration: 'Jul 2022 – Aug 2023',
    title: 'Software Engineer',
    bullets: bennett_bullets,
    tags: 'Python, C++ (CUDA), React, SQL'
  },
  {
    company: 'GPOne Consulting',
    location: 'Brisbane, AU',
    duration: 'Mar 2021 – Mar 2022',
    title: 'Software Engineer',
    bullets: gpone_bullets,
    tags: 'Python, ArcGIS Pro, AWS EC2, PostgreSQL, Figma'
  }
];

function Experience() {
  // Single job card — handles either a flat role (title + bullets) or a
  // multi-role promotion path (roles: [{ title, duration, bullets }])
  const ExperienceItem = ({ company, location, duration, title, bullets, roles, tags }) => {
    return (
      <div className="mb-8 p-6 border-accent border-2 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-1">
          <h3 className="text-2xl font-bold text-accent">{company}</h3>
          <p className="text-base text-text">{duration}</p>
        </div>
        <p className="text-base text-text mb-4">{location}</p>

        {/* Single-role company */}
        {!roles && (
          <div className="mb-2">
            <p className="text-lg font-medium text-text mb-2">{title}</p>
            <ul className="list-disc list-inside space-y-1">
              {bullets.map((b, i) => (
                <li key={i} className="text-text text-base">{b}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Multi-role promotion path (e.g. ABS) */}
        {roles && (
          <div className="space-y-5">
            {roles.map((role, i) => (
              <div key={i} className="pl-4 border-l-2 border-accent">
                <div className="flex justify-between items-center mb-1">
                  <p className="text-lg font-semibold text-text">{role.title}</p>
                  <p className="text-sm text-text italic">{role.duration}</p>
                </div>
                <ul className="list-disc list-inside space-y-1">
                  {role.bullets.map((b, j) => (
                    <li key={j} className="text-text text-base">{b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        <p className="text-text text-base font-bold mt-4">Tech stack: {tags}</p>
      </div>
    );
  };

  return (
    <div className="flex flex-col flex-grow w-screen overflow-y-scroll items-center justify-around">
      <Header />
      <main className="bg-topography w-screen h-full flex flex-col items-center justify-center">
        <div className="w-screen h-24" />
        <div className="max-w-4xl mx-auto rounded-xl shadow-2xl p-10">
          <div className="flex items-center mb-8">
            <span className="text-2xl mr-2">💼</span>
            <h2 className="text-2xl font-bold text-accent">Experience</h2>
          </div>
          {experience.map((job, index) => (
            <ExperienceItem key={index} {...job} />
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default Experience;
